import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

const referralApi = axios.create({
  baseURL: `${config.API_BASE_URL}/cellcog/referrals`,
});

setupAxiosInterceptor(referralApi);

// ============= User Endpoints =============

/**
 * Get user's referrals, stats, and program info
 * @returns {Promise} Response with program, stats, my_referrals, referred_by
 */
export const getReferrals = () => {
  return referralApi.get('/');
};

/**
 * Send email invitation to a new user
 * @param {string} email - Email address to invite
 * @returns {Promise} Response with success, message, referral_id
 */
export const sendInvitation = (email) => {
  return referralApi.post('/invite', { email });
};

/**
 * Resend invitation email for pending referral
 * @param {string} referralId - Referral document ID
 * @returns {Promise} Response with success, message
 */
export const resendInvitation = (referralId) => {
  return referralApi.post(`/${referralId}/resend`);
};

/**
 * Redeem referral bonus (500 credits to permanent wallet)
 * @param {string} referralId - Referral document ID
 * @returns {Promise} Response with success, credits_awarded, new_permanent_balance, message
 */
export const redeemReferral = (referralId) => {
  return referralApi.post(`/${referralId}/redeem`);
};

/**
 * Validate referral code (public endpoint)
 * @param {string} code - Referral code to validate
 * @returns {Promise} Response with valid, referrer_display_name, referrer_email_masked, reason
 */
export const validateReferralCode = (code) => {
  return referralApi.get(`/validate/${code}`);
};

// ============= Admin Endpoints =============

const adminReferralApi = axios.create({
  baseURL: `${config.API_BASE_URL}/cellcog/admin/referrals`,
});

setupAxiosInterceptor(adminReferralApi);

/**
 * Get program-wide statistics (admin only)
 * @returns {Promise} Response with stats
 */
export const getAdminStats = () => {
  return adminReferralApi.get('/stats');
};

/**
 * Get fraud flags for review (admin only)
 * @param {Object} params - Query parameters (flag_type, severity, reviewed, limit, offset)
 * @returns {Promise} Response with flags array
 */
export const getFraudFlags = (params = {}) => {
  return adminReferralApi.get('/fraud-flags', { params });
};

/**
 * Get users' referral program status (admin only)
 * @param {Object} params - Query parameters (search, status, limit, offset)
 * @returns {Promise} Response with users array
 */
export const getAdminUsers = (params = {}) => {
  return adminReferralApi.get('/users', { params });
};

/**
 * Disable user's referral program (admin only)
 * @param {string} userId - User ID to disable
 * @param {string} reason - Reason for disabling
 * @returns {Promise} Response with message, credits_forfeited
 */
export const disableUserProgram = (userId, reason) => {
  return adminReferralApi.post(`/users/${userId}/disable`, { reason });
};

/**
 * Enable user's referral program (admin only)
 * @param {string} userId - User ID to enable
 * @returns {Promise} Response with message
 */
export const enableUserProgram = (userId) => {
  return adminReferralApi.post(`/users/${userId}/enable`);
};

/**
 * Review fraud flag (admin only)
 * @param {string} flagId - Fraud flag ID
 * @param {string} action - Review action (APPROVED, FLAGGED, DISABLED_USER)
 * @param {string} notes - Optional review notes
 * @returns {Promise} Response with message
 */
export const reviewFraudFlag = (flagId, action, notes = null) => {
  return adminReferralApi.post(`/fraud-flags/${flagId}/review`, { action, notes });
};
